import React from "react";
import {
  Badge,
  Box,
  Divider,
  Flex,
  HStack,
  Icon,
  IconButton,
  Text,
  Tooltip,
  VStack,
} from "@chakra-ui/react";
import { InfoOutlineIcon } from "@chakra-ui/icons";
import { CptAudit } from "../../types/cptAudit.types";
import { formatSmartDateTime } from "../../utils/deliveryTimeUtils";
import {
  formatDayLabel,
  formatMinutesShort,
  formatMovementKind,
  formatScheduleRow,
  getReadyTimeBreakdown,
  getTodayIsoDate,
  groupScheduleRowsByDay,
  parseSrtFormula,
} from "../../utils/cptAuditTooltipUtils";

const SECTION_SPACING = 2;
const PANEL_WIDTH = "360px";
const MUTED_LABEL_PROPS = {
  fontSize: "xs",
  color: "gray.500",
  lineHeight: "1.3",
} as const;
const TOOLTIP_TEXT_PROPS = {
  fontSize: "xs",
  color: "white",
  lineHeight: "1.35",
} as const;

interface CptAuditTooltipContentProps {
  audit: CptAudit;
}

interface BreakdownRowProps {
  label: string;
  value: string;
  trailing?: React.ReactNode;
  labelColor?: string;
  valueColor?: string;
  labelTooltip?: string;
}

const BreakdownRow: React.FC<BreakdownRowProps> = ({
  label,
  value,
  trailing,
  labelColor = "gray.500",
  valueColor = "gray.800",
  labelTooltip,
}) => {
  const labelNode = (
    <Text
      fontSize="xs"
      color={labelColor}
      lineHeight="1.3"
      noOfLines={1}
      cursor={labelTooltip ? "pointer" : undefined}
    >
      {label}
    </Text>
  );

  return (
    <Flex justify="space-between" align="center" gap={3}>
      <HStack spacing={1} align="center" minW={0}>
        {labelTooltip ? (
          <Tooltip label={labelTooltip} hasArrow placement="top" openDelay={150}>
            {labelNode}
          </Tooltip>
        ) : (
          labelNode
        )}
        {trailing}
      </HStack>
      <Text
        fontSize="xs"
        color={valueColor}
        fontWeight="500"
        lineHeight="1.3"
        whiteSpace="nowrap"
      >
        {value}
      </Text>
    </Flex>
  );
};

const LabelValueRow: React.FC<{
  label: string;
  value: string;
  valueFontWeight?: string;
  valueFontSize?: string;
}> = ({
  label,
  value,
  valueFontWeight = "500",
  valueFontSize = "xs",
}) => (
  <Flex justify="space-between" align="center" gap={3}>
    <Text {...MUTED_LABEL_PROPS} flexShrink={0}>
      {label}
    </Text>
    <Text
      fontSize={valueFontSize}
      fontWeight={valueFontWeight}
      color="gray.800"
      lineHeight="1.3"
      textAlign="right"
    >
      {value}
    </Text>
  </Flex>
);

const SectionDivider: React.FC = () => (
  <Divider borderColor="gray.200" opacity={0.9} />
);

const SrtFormulaTooltipContent: React.FC<{ formula: string }> = ({
  formula,
}) => {
  const parsed = parseSrtFormula(formula);

  if (!parsed) {
    return (
      <Box py={0.5}>
        <Text {...TOOLTIP_TEXT_PROPS} fontWeight="600" mb={1}>
          SRT Calculation
        </Text>
        <Text {...TOOLTIP_TEXT_PROPS}>{formula}</Text>
      </Box>
    );
  }

  return (
    <Box py={0.5} minW="220px">
      <Text {...TOOLTIP_TEXT_PROPS} fontWeight="600" mb={1.5}>
        SRT Calculation
      </Text>
      <VStack align="stretch" spacing={0.5}>
        <BreakdownRow
          label="Base Time"
          value={formatMinutesShort(parsed.baseTimeMinutes)}
          labelColor="whiteAlpha.800"
          valueColor="white"
        />
        <BreakdownRow
          label="Line Items"
          value={String(parsed.lineItems)}
          labelColor="whiteAlpha.800"
          valueColor="white"
        />
        <BreakdownRow
          label="Per Item"
          value={formatMinutesShort(parsed.perItemMinutes)}
          labelColor="whiteAlpha.800"
          valueColor="white"
        />
      </VStack>
      <Text {...TOOLTIP_TEXT_PROPS} mt={1.5}>
        {parsed.formula} min
      </Text>
    </Box>
  );
};

const InfoTooltipIcon: React.FC<{
  label: React.ReactNode;
  ariaLabel: string;
}> = ({ label, ariaLabel }) => (
  <Tooltip
    hasArrow
    placement="right"
    openDelay={150}
    zIndex={1700}
    label={label}
  >
    <IconButton
      variant="unstyled"
      display="inline-flex"
      alignItems="center"
      justifyContent="center"
      minW="auto"
      h="auto"
      cursor="pointer"
      onClick={(event: React.MouseEvent) => event.stopPropagation()}
      aria-label={ariaLabel}
      icon={<InfoOutlineIcon boxSize={2.5} color="blue.500" />}
    />
  </Tooltip>
);

const ScheduleDirectionIcon: React.FC<{ direction: "in" | "out" }> = ({
  direction,
}) => (
  <Icon viewBox="0 0 24 24" boxSize="11px" mr={1}>
    <path
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="4"
      d={
        direction === "out"
          ? "m16 17l5-5l-5-5m5 5H9m0 9H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"
          : "m10 17l5-5l-5-5m5 5H3m12-9h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"
      }
    />
  </Icon>
);

const ScheduleActionBadge: React.FC<{ action: "in" | "out" }> = ({
  action,
}) => {
  const isOut = action === "out";

  return (
    <Badge
      colorScheme={isOut ? "red" : "green"}
      variant="outline"
      bg={isOut ? "red.50" : "green.50"}
      borderRadius="full"
      textTransform="none"
      fontSize="xs"
      fontWeight="600"
      px={2}
      py={0.5}
      display="inline-flex"
      alignItems="center"
      flexShrink={0}
      lineHeight="1.2"
    >
      <ScheduleDirectionIcon direction={action} />
      {isOut ? "Out" : "In"}
    </Badge>
  );
};

const ScheduleCard: React.FC<{
  schedule: ReturnType<typeof formatScheduleRow>;
}> = ({ schedule }) => {
  const [routeLabel, slotLabel] = schedule.routeSlotLabel.split(" • ");

  return (
    <Box
      px={3}
      py={1.5}
      border="1px solid"
      borderColor="gray.200"
      borderRadius="xl"
      bg="gray.50"
    >
      <Flex justify="space-between" align="center" gap={2}>
        <HStack spacing={1.5} minW={0} flex={1}>
          <Tooltip
            label={schedule.businessName}
            hasArrow
            placement="top"
            openDelay={150}
          >
            <Text
              fontSize="sm"
              fontWeight="600"
              color="gray.800"
              noOfLines={1}
              minW={0}
            >
              {schedule.businessName}
            </Text>
          </Tooltip>
          <ScheduleActionBadge action={schedule.action} />
        </HStack>
        <Text fontSize="xs" color="gray.500" whiteSpace="nowrap" flexShrink={0}>
          {routeLabel} · {slotLabel}
        </Text>
      </Flex>
      <Text fontSize="xs" color="gray.500" mt={0.5}>
        In Time: {schedule.cutoffIn}
        {schedule.cutoffInDayOffsetSuffix}, Out Time: {schedule.cutoffOut}
      </Text>
    </Box>
  );
};

export const CptAuditTooltipContent: React.FC<CptAuditTooltipContentProps> = ({
  audit,
}) => {
  const breakdown = getReadyTimeBreakdown(audit);
  const todayIso = getTodayIsoDate();
  const scheduleRows = audit.selectedSchedules.map((schedule, index) =>
    formatScheduleRow(schedule, index),
  );
  const scheduleGroups = groupScheduleRowsByDay(scheduleRows);

  return (
    <Box minW={PANEL_WIDTH} maxW={PANEL_WIDTH}>
      <Text
        fontSize="xs"
        fontWeight="700"
        color="gray.800"
        letterSpacing="wide"
      >
        Delivery Time Calculation
      </Text>

      <Box mt={1.5} mb={SECTION_SPACING}>
        <LabelValueRow
          label="Predicted Material Ready Time"
          value={formatSmartDateTime(audit.predictedMaterialReadyAt)}
          valueFontWeight="600"
        />
      </Box>

      <SectionDivider />

      <Box my={SECTION_SPACING}>
        <Text fontSize="xs" fontWeight="600" color="gray.700" mb={1}>
          Ready Time Breakdown
        </Text>
        <VStack align="stretch" spacing={0.5}>
          <BreakdownRow
            label="CRT"
            value={formatMinutesShort(breakdown.crtMinutes)}
            trailing={
              <InfoTooltipIcon
                label="Customer Response Time"
                ariaLabel="Customer Response Time"
              />
            }
          />
          <BreakdownRow
            label="SRT"
            value={formatMinutesShort(breakdown.srtMinutes)}
            trailing={
              <InfoTooltipIcon
                label={<SrtFormulaTooltipContent formula={audit.srtFormula} />}
                ariaLabel="View SRT calculation breakdown"
              />
            }
          />
          <BreakdownRow
            label="QRT"
            value={formatMinutesShort(breakdown.qrtMinutes)}
            trailing={
              <InfoTooltipIcon
                label="QC Review Time"
                ariaLabel="QC Review Time"
              />
            }
          />
        </VStack>
        <Divider borderColor="gray.300" my={1} />
        <BreakdownRow
          label="Total"
          value={formatMinutesShort(breakdown.totalMinutes)}
        />
      </Box>

      <SectionDivider />

      <Box my={SECTION_SPACING}>
        <LabelValueRow
          label="Movement"
          value={formatMovementKind(audit.movementKind)}
        />
      </Box>

      {scheduleRows.length > 0 && (
        <>
          <SectionDivider />
          <Box my={SECTION_SPACING}>
            <Text fontSize="xs" fontWeight="600" color="gray.700" mb={1}>
              {scheduleRows.length === 1
                ? "Selected Schedule"
                : "Selected Schedules"}
            </Text>
            <VStack align="stretch" spacing={2}>
              {scheduleGroups.map((group, groupIndex) => (
                <Box key={group.dispatchDate || `group-${groupIndex}`}>
                  {group.dispatchDate ? (
                    <Text
                      fontSize="xs"
                      fontWeight="700"
                      color="gray.600"
                      mb={1}
                    >
                      {formatDayLabel(group.dispatchDate, todayIso)}
                    </Text>
                  ) : null}
                  <VStack align="stretch" spacing={1}>
                    {group.rows.map((schedule) => (
                      <ScheduleCard
                        key={`${schedule.businessName}-${schedule.routeSlotLabel}-${schedule.cutoffOut}`}
                        schedule={schedule}
                      />
                    ))}
                  </VStack>
                </Box>
              ))}
            </VStack>
          </Box>
        </>
      )}

      <SectionDivider />

      <Box mt={SECTION_SPACING}>
        {audit.destinationSupplierReadyTime != null && (
          <Box mb={1}>
            <LabelValueRow
              label="Destination supplier ready time"
              value={formatMinutesShort(audit.destinationSupplierReadyTime)}
            />
          </Box>
        )}
        <LabelValueRow
          label="Garaaz delivery time"
          value={formatMinutesShort(audit.garaazDeliveryTime)}
        />
      </Box>
    </Box>
  );
};
